import { type ClientId } from '../../core/ClientId';
import { type TimeData } from '../../core/TimeData';
import { type PnlProcessorContext } from '../context/context';
import { PnlProcessor, type PnlProcessorResult } from '../processor';
import {
  getEtsTransitionFactor,
  getShipmentEtsFactor,
  DEFAULT_CARBON_FACTOR,
} from '../../shipment/ets';
import { getCostSharingContext } from '../costSharing/costSharing';
import { getBunkerConsumptionContext } from '../bunker/bunkerProcessor';
import type {
  Co2Emission,
  EmissionCostLocationRef,
  EuEtsCost,
  FuelEuCost,
} from './co2Cost';
import {
  calculateFuelEuEmissions,
  getFuelEuTotals,
  FUEL_EU_COMPLIANCE_PRICE_EUR,
  type FuelEuEmissions,
  calculateFuelEuComplianceBalance,
  calculateFuelEuCompensationCost,
  getFuelEuCountryFactor,
  checkVesselMeetsFuelEuCriteria,
} from './fuelEu';
import { hToMs } from '../../../lib/time';
import { type ScenarioLocation } from '../../../lib/planningData';
import { Currency } from '@seaber/common/graphql/types';
import { convertMonetaryValue } from '../../../state/zustand/currencyRates';
import { assertDefined } from '@seaber/common/lib/generics';

const PROCESSOR_METADATA = {
  id: 'co2-cost',
  dependencies: ['bunker-cost', 'cost-sharing'],
};

export interface BunkerGrade {
  /** Carbon factor used for EU ETS and Fuel EU calculations (tank-to-wake) */
  carbonFactor: number | null;
  /** Methane emission factor for tank-to-wake calculations */
  ch4Factor: number | null;
  /** Id of the Bunker Override */
  id: number;
  /** Lower calorific value for Fuel EU calculations */
  lcv: number | null;
  /** Nitrous oxide emission factor tank-to-wake calculations */
  n2oFactor: number | null;
  /** Well-to-tank emission factor */
  wttFactor: number | null;
}

const emissionFactorLabels = {
  n2oFactor: 'Nitrous Oxide grade',
  ch4Factor: 'Methane grade',
  carbonFactor: 'Carbon factor',
  lcv: 'LCV Grade',
  wttFactor: 'CO2eq WtT grade',
} as const;

type EmissionFactorKey = keyof typeof emissionFactorLabels;

/**
 * Input parameters for the CO2 processor
 */
export interface Co2ProcessorInput {
  /** All bunker grade override entries for the organisation */
  bunkerGradeById: Map<number, BunkerGrade>;
  /** Carbon price data from external source */
  carbonPrice: {
    /** Carbon price per tonne in base currency */
    carbonPricePerTonne: number | null;
    /** Time when the price was last updated */
    updatedAt: string | null;
    /** Whether the price is from an override (vs regular source) */
    isOverride: boolean;
  };
}

/**
 * Result object returned by the CO2 processor
 */
interface Co2ProcessorResult
  extends PnlProcessorResult<EuEtsCost | FuelEuCost, undefined> {
  co2Emissions: Co2Emission[];
}

type WithLocations<T> = T & {
  locations: EmissionCostLocationRef[];
};

interface ShipmentEmissions {
  /** CO2 Emissions for EU ETS calculation, keyed by port call id for port emissions and timedata key for leg emissions */
  co2Emissions: Map<string, WithLocations<Co2Emission>[]>;
  /** Emissions for Fuel EU calculation, keyed by port call id for port emissions and timedata key for leg emissions */
  fuelEuEmissions: Map<string, WithLocations<FuelEuEmissions>[]>;
  /** Start year of the shipment */
  startYear: number;
}

type EmissionsByShipment = Map<ClientId, ShipmentEmissions>;

/**
 * Processes time data to calculate CO2 emissions and related costs.
 *
 * This includes both regular emission tracking and EU ETS costs.
 */
export class Co2CostProcessor extends PnlProcessor<
  EuEtsCost | FuelEuCost,
  undefined,
  Co2ProcessorResult
> {
  private state: {
    emissionsByShipment: EmissionsByShipment;
    fuelEuCompliancePrice: number;
    missingEmissionFactorsByBunkerGrade: Map<string, EmissionFactorKey[]>;
    previousLocation: EmissionCostLocationRef | null;
  } = {
    emissionsByShipment: new Map(),
    fuelEuCompliancePrice: 0,
    missingEmissionFactorsByBunkerGrade: new Map(),
    previousLocation: null,
  };

  public result: Co2ProcessorResult = {
    costs: [],
    errors: [],
    income: undefined,
    co2Emissions: [],
  };

  private input: Co2ProcessorInput;

  constructor(input: Co2ProcessorInput, context: PnlProcessorContext) {
    super(PROCESSOR_METADATA, context);

    this.input = input;

    this.state.fuelEuCompliancePrice = convertMonetaryValue(
      FUEL_EU_COMPLIANCE_PRICE_EUR,
      Currency.EUR,
      context.currency.rates,
    );
  }

  processEvent(td: TimeData): void {
    const { consumptionByTd } = getBunkerConsumptionContext(this.context);
    const consumptions = consumptionByTd.get(td) ?? [];
    const { legShipmentId, eventShipmentId } = this.getTimeDataShipmentIds(td);

    // Ensure shipment exists in the map
    if (legShipmentId && !this.state.emissionsByShipment.has(legShipmentId)) {
      // We assume that the first encountered timedata associated with the
      // shipment is the first one as we're processing events in order
      this.initializeShipmentEmissionData(
        legShipmentId,
        // TODO: We should track shipment start time in cost sharing processor
        // instead since we probably need similar information elsewhere
        new Date(td.arriveInPort - hToMs(td.hoursAtSea)).getUTCFullYear(),
      );
    }

    if (
      eventShipmentId &&
      !this.state.emissionsByShipment.has(eventShipmentId)
    ) {
      this.initializeShipmentEmissionData(
        eventShipmentId,
        new Date(td.start).getUTCFullYear(),
      );
    }

    const eventLocations = extractLocationsFromEvent(
      td,
      this.context.locationById,
    );

    for (const consumption of consumptions) {
      const key = consumption.type === 'PORT' ? td.event.portCallId : td.key;
      if (!key) continue;

      const bunkerGrade = this.input.bunkerGradeById.get(
        consumption.bunkerGradeId,
      );

      if (!bunkerGrade) {
        this.result.errors.push({
          error: `Bunker grade not found with id ${consumption.bunkerGradeId}`,
          processor: PROCESSOR_METADATA.id,
        });
        continue;
      }

      // Get related locations for calculating country factor later
      const eventArrivalLocation = eventLocations[0];
      const prevLocation = this.state.previousLocation;

      const locations =
        consumption.type === 'PORT'
          ? [eventArrivalLocation]
          : [...(prevLocation ? [prevLocation] : []), eventArrivalLocation];

      const carbonFactor = bunkerGrade.carbonFactor || DEFAULT_CARBON_FACTOR;
      const emittedCo2Tonnes = consumption.consumedBunkerTonnes * carbonFactor;

      const targetShipmentId =
        consumption.type === 'BALLAST' || consumption.type === 'LADEN'
          ? legShipmentId
          : eventShipmentId;

      if (!targetShipmentId) continue;

      const shipmentData = this.state.emissionsByShipment.get(targetShipmentId);
      assertDefined(shipmentData, 'Shipment data should be defined');

      const co2Emission: Co2Emission = {
        portCallId: td.event.portCallId,
        shipmentId: targetShipmentId,
        consumption,
        carbonFactor,
        emittedCo2Tonnes,
      };

      let shipmentCo2Emissions = shipmentData.co2Emissions.get(key);

      if (!shipmentCo2Emissions) {
        shipmentCo2Emissions = [];
        shipmentData.co2Emissions.set(key, shipmentCo2Emissions);
      }

      shipmentCo2Emissions.push({
        ...co2Emission,
        locations,
      });

      this.result.co2Emissions.push(co2Emission);

      // Track bunker grades with missing emission factors
      if (
        bunkerGrade.carbonFactor === null ||
        bunkerGrade.ch4Factor === null ||
        bunkerGrade.lcv === null ||
        bunkerGrade.n2oFactor === null ||
        bunkerGrade.wttFactor === null
      ) {
        if (
          !this.state.missingEmissionFactorsByBunkerGrade.has(
            consumption.bunkerGrade,
          )
        ) {
          this.state.missingEmissionFactorsByBunkerGrade.set(
            consumption.bunkerGrade,
            (
              [
                'carbonFactor',
                'ch4Factor',
                'lcv',
                'n2oFactor',
                'wttFactor',
              ] as const
            ).filter((key) => {
              return (
                bunkerGrade[key] === null || bunkerGrade[key] === undefined
              );
            }),
          );
        }
        continue;
      }

      // Calculate FuelEU emissions if enabled
      const fuelEuEmissions = calculateFuelEuEmissions(
        consumption.consumedBunkerTonnes,
        {
          carbonFactor: bunkerGrade.carbonFactor,
          ch4Factor: bunkerGrade.ch4Factor,
          lcv: bunkerGrade.lcv,
          n2oFactor: bunkerGrade.n2oFactor,
          wttFactor: bunkerGrade.wttFactor,
        },
      );

      let shipmentFuelEuEmissions = shipmentData.fuelEuEmissions.get(key);

      if (!shipmentFuelEuEmissions) {
        shipmentFuelEuEmissions = [];
        shipmentData.fuelEuEmissions.set(key, shipmentFuelEuEmissions);
      }

      shipmentFuelEuEmissions.push({
        ...fuelEuEmissions,
        locations,
      });
    }

    this.state.previousLocation =
      eventLocations.at(-1) ?? this.state.previousLocation;
  }

  finalize() {
    const isVesselGrossTonnageOver5000 =
      (this.context.vessel?.grossTonnage ?? 0) >= 5000;

    const shouldCalculateEtsCosts = isVesselGrossTonnageOver5000;
    const shouldCalculateFuelEuCosts =
      this.context.vessel &&
      checkVesselMeetsFuelEuCriteria(this.context.vessel);

    for (const [shipmentId, shipmentData] of this.state.emissionsByShipment) {
      const { co2Emissions, startYear, fuelEuEmissions } = shipmentData;

      // Calculate EU ETS costs
      if (shouldCalculateEtsCosts) {
        for (const [_, emissions] of co2Emissions) {
          this.calculateEtsCosts(shipmentId, emissions, startYear);
        }
      }

      // Calculate Fuel EU costs
      if (shouldCalculateFuelEuCosts) {
        for (const [_, emissions] of fuelEuEmissions) {
          this.calculateFuelEuCosts(shipmentId, emissions, startYear);
        }
      }
    }

    // Add errors for missing emission factors for bunker grades
    for (const [
      bunkerGrade,
      missingFields,
    ] of this.state.missingEmissionFactorsByBunkerGrade.entries()) {
      this.result.errors.push({
        error: `Bunker grade "${bunkerGrade}" is missing properties required for Fuel EU calculation: ${missingFields.map((key) => emissionFactorLabels[key]).join(', ')}`,
        processor: PROCESSOR_METADATA.id,
      });
    }
  }

  private calculateEtsCosts(
    shipmentId: ClientId,
    co2Emissions: WithLocations<Co2Emission>[],
    startYear: number,
  ) {
    // We expect emissions to be grouped so that they all share the same locations
    const locations = co2Emissions.at(0)?.locations ?? [];
    if (locations.length === 0) {
      console.error('No locations found for emission', co2Emissions.at(0));
      return;
    }

    const carbonPricePerTonne = this.input.carbonPrice.carbonPricePerTonne;
    if (carbonPricePerTonne === null) return;

    const totalCo2Emissions = co2Emissions.reduce(
      (sum, { emittedCo2Tonnes }) => sum + emittedCo2Tonnes,
      0,
    );

    // Skip if there are no emissions to calculate costs for
    if (totalCo2Emissions <= 0) return;

    // Calculate EU ETS transition factor for this year
    const transitionFactor = getEtsTransitionFactor(startYear);

    // Skip if no transition factor applicable yet
    if (transitionFactor <= 0) return;

    // Calculate EU ETS factor based on countries and vessel
    const etsFactor = getShipmentEtsFactor(
      locations.map((loc) => loc.country),
      this.context.vessel?.grossTonnage ?? 0,
    );

    // Skip if no ETS factor applicable
    if (etsFactor <= 0) return;

    // Calculate EU ETS cost
    const etsCost =
      totalCo2Emissions * carbonPricePerTonne * transitionFactor * etsFactor;

    // Create EU ETS cost record
    const co2EtsCost: EuEtsCost = {
      amount: etsCost,
      carbonPricePerTonne: carbonPricePerTonne,
      category: 'eu_ets_cost',
      etsFactor,
      isPriceOverride: this.input.carbonPrice.isOverride,
      locations,
      portCallId: null,
      priceUpdatedAt: this.input.carbonPrice.updatedAt,
      shipmentId,
      totalCo2Tonnes: totalCo2Emissions,
      transitionFactor,
      type: 'cost',
      year: startYear,
    };

    this.result.costs.push(co2EtsCost);
  }

  /**
   * Calculate FuelEU costs for a shipment
   */
  private calculateFuelEuCosts(
    shipmentId: ClientId,
    emissions: WithLocations<FuelEuEmissions>[],
    startYear: number,
  ): void {
    // We expect emissions to be grouped so that they all share the same locations
    const locations = emissions.at(0)?.locations ?? [];
    if (locations.length === 0) {
      console.log('No locations found for emission', emissions.at(0));
      return;
    }

    const countryFactor = getFuelEuCountryFactor(
      locations.map((loc) => loc.country),
    );

    const totals = getFuelEuTotals(emissions, countryFactor);

    const { complianceBalance, targetGhgIntensity } =
      calculateFuelEuComplianceBalance(totals.ghgIntensity, startYear);

    const cost = calculateFuelEuCompensationCost(
      complianceBalance,
      totals.ghgIntensity,
      totals.effectiveTotalEnergyMJ,
      this.state.fuelEuCompliancePrice,
    );

    const fuelEuCost: FuelEuCost = {
      amount: cost,
      category: 'fuel_eu_cost',
      complianceBalance,
      compliancePricePerUnit: FUEL_EU_COMPLIANCE_PRICE_EUR,
      ghgIntensity: totals.ghgIntensity,
      countryFactor,
      locations,
      portCallId: null,
      ghgIntensityTarget: targetGhgIntensity,
      shipmentId,
      totalEnergyMJ: totals.totalEnergyMJ,
      totalTankToWakeEmissions: totals.totalTankToWakeEmissions,
      totalWellToTankEmissions: totals.totalWellToTankEmissions,
      type: 'cost',
      year: startYear,
    };

    this.result.costs.push(fuelEuCost);
  }

  getResult(): Co2ProcessorResult {
    return this.result;
  }

  private initializeShipmentEmissionData(
    shipmentId: ClientId,
    startYear: number,
  ) {
    this.state.emissionsByShipment.set(shipmentId, {
      co2Emissions: new Map(),
      fuelEuEmissions: new Map(),
      startYear,
    });
  }

  /**
   * Get shipment ID from time data using cost sharing context
   */
  private getTimeDataShipmentIds(td: TimeData): {
    eventShipmentId: ClientId | null;
    legShipmentId: ClientId | null;
  } {
    const { costSharingDetailsByTd } = getCostSharingContext(this.context);
    const costSharingDetails = costSharingDetailsByTd.get(td);

    return {
      eventShipmentId: costSharingDetails?.eventShipmentId ?? null,
      legShipmentId: costSharingDetails?.legShipmentId ?? null,
    };
  }
}

export function extractLocationsFromEvent(
  td: TimeData,
  locationById: Map<number, ScenarioLocation>,
): EmissionCostLocationRef[] {
  const locations: EmissionCostLocationRef[] = [];
  const { event } = td;

  if ('location' in event && event.location) {
    const { location } = event;

    if (location) {
      locations.push({
        id: location.id,
        name: location.name,
        country: location.country || '',
      });
    }
  } else if ('locationAId' in event && 'locationBId' in event) {
    const { locationAId, locationBId } = event;
    const locationA = locationById.get(locationAId);
    const locationB = locationById.get(locationBId);
    if (locationA)
      locations.push({
        id: locationA.id,
        name: locationA.name,
        country: locationA.country || '',
      });
    if (locationB)
      locations.push({
        id: locationB.id,
        name: locationB.name,
        country: locationB.country || '',
      });
  }
  return locations;
}
